import type { ReactNode } from 'react';
import { Clock, CreditCard, Eye, Handshake, Key } from '../icons';

interface ComparisonColumn {
  title: string;
  desc: string;
}

export interface ComparisonRow {
  category: string;
  icon: ReactNode;
  agency: ComparisonColumn;
  consultants: ComparisonColumn;
  augment: ComparisonColumn;
}

/**
 * Three-way comparison. The two rival columns are a full-service agency and
 * doing it in-house or yourself, the two things this offer actually displaces.
 */
export const COMPARISON_DATA: readonly ComparisonRow[] = [
  {
    category: 'Cost',
    icon: <CreditCard size={24} strokeWidth={1.5} />,
    agency: {
      title: '$42K to $96K a year.',
      desc: 'Retainer plus scope creep, and the number climbs with every extra format you ask for.',
    },
    consultants: {
      title: '$211K to $320K fully loaded.',
      desc: 'A social media manager and a designer, before you add a copywriter for the long-form.',
    },
    augment: {
      title: 'A fraction of either.',
      desc: 'One retainer covering content, copywriting and production, with Meta ad spend the only thing carrying a revenue share.',
    },
  },
  {
    category: 'Your Time',
    icon: <Clock size={24} strokeWidth={1.5} />,
    agency: {
      title: '3 to 8 hours a week.',
      desc: 'Briefing, review cycles, status calls. You are managing the work, not free of it.',
    },
    consultants: {
      title: '15 to 20 hours a week.',
      desc: 'If you are doing it yourself, this is most of a working day, every week, forever.',
    },
    augment: {
      title: 'About two hours a week.',
      desc: 'Review, approval, and a few phone clips from the job. No shoot days, no production setup.',
    },
  },
  {
    category: 'Ownership',
    icon: <Key size={24} strokeWidth={1.5} />,
    agency: {
      title: 'You own nothing at the end.',
      desc: 'The files, the history and the systems stay with them when the retainer stops.',
    },
    consultants: {
      title: 'It leaves when they leave.',
      desc: 'The process lived in one person, and it walks out the door with them.',
    },
    augment: {
      title: 'Yours if you leave.',
      desc: 'Files, history and reports go with you. Month-to-month, cancel with 30 days notice.',
    },
  },
  {
    category: 'Does It Sound Like You',
    icon: <Eye size={24} strokeWidth={1.5} />,
    agency: {
      title: 'Written by someone outside it.',
      desc: 'A junior who has never sat on one of your sales calls, guessing at what your customers ask.',
    },
    consultants: {
      title: 'Generic AI output.',
      desc: 'A tool with no context on your business, which is why the output read as fake last time.',
    },
    augment: {
      title: 'Built from your own calls.',
      desc: 'Grounded in your recorded objections and your documented voice, human-checked before it goes live.',
    },
  },
  {
    category: 'What Happens Over Time',
    icon: <Handshake size={24} strokeWidth={1.5} />,
    agency: {
      title: 'It resets when you stop.',
      desc: 'You are renting reach. The month you cancel is the month it goes quiet.',
    },
    consultants: {
      title: 'It stops when you get busy.',
      desc: 'Client work always wins, so the posting stops exactly when things are going well.',
    },
    augment: {
      title: 'It compounds.',
      desc: 'A weekly loop that learns what worked and repeats it on purpose, on top of a library that keeps earning.',
    },
  },
];
